import React, { useEffect, useMemo, useRef, useState } from 'react';
import './Playgrounds.css';
import BasicCli from '../../main/components/basicCli/BasicCli';
import EditModal from '../../main/components/modal/edit/EditModal';
import DeleteModal from '../../main/components/modal/delete/DeleteModal';
import eFormType from '../../main/assets/enums/eFormType';
import GeneralInput from '../../main/components/generalInput/GeneralInput';
import axiosInstance from '../../main/initializers/axios';
import { ApiConstants } from '../../main/constants/ApiConstants';
import { IPlayground, IPlaygroundSession } from '../../main/interfaces/IPlayground';
import { SubmitHandler, useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { getMetadataFileUrl, loadEntityGalleryMap } from '../../main/utils/entityGallery';
import { compressImageIfNeeded, DEFAULT_IMAGE_MAX_KB } from '../../main/utils/imageCompression';
import useGetUser from '../../main/hooks/useGetUser';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { IconButton, Tooltip } from '@mui/material';

type PlaygroundFormValues = IPlayground;

type PlaygroundSessionView = IPlaygroundSession & {
  price?: number | null;
};

const ENDING_SOON_MS = 5 * 60 * 1000;
const MAX_SESSION_PHOTO_BYTES = DEFAULT_IMAGE_MAX_KB * 1024;

const formatDateTime = (value?: string | null) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleString();
};

const getTimeLeftMs = (session: IPlaygroundSession, nowMs: number) => {
  if (!session.endTime) return null;
  const endTimeMs = new Date(session.endTime).getTime();
  if (Number.isNaN(endTimeMs)) return null;
  return endTimeMs - nowMs;
};

const formatTimeLeft = (timeLeftMs: number | null) => {
  if (timeLeftMs === null) return '-';
  if (timeLeftMs <= 0) return '00:00';

  const totalSeconds = Math.ceil(timeLeftMs / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  const pad = (value: number) => String(value).padStart(2, '0');

  return hours > 0 ? `${hours}:${pad(minutes)}:${pad(seconds)}` : `${pad(minutes)}:${pad(seconds)}`;
};

const formatDateTimeLocalInput = (date: Date) => {
  const pad = (value: number) => String(value).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

const parsePositiveNumber = (value: unknown, fallback: number) => {
  if (value === undefined || value === null || String(value) === '') return fallback;
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
};

const Playgrounds = () => {
  const { i18n } = useTranslation();
  const currentUser = useGetUser();
  const [playgrounds, setPlaygrounds] = useState<PlaygroundFormValues[]>([]);
  const [selectedPlayground, setSelectedPlayground] = useState<PlaygroundFormValues | null>(null);
  const [sessions, setSessions] = useState<PlaygroundSessionView[]>([]);
  const [isPlaygroundModalOpen, setIsPlaygroundModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isSessionModalOpen, setIsSessionModalOpen] = useState(false);
  const [selectedSession, setSelectedSession] = useState<PlaygroundSessionView | null>(null);
  const [sessionPhotoFile, setSessionPhotoFile] = useState<File | null>(null);
  const [sessionPhotoPreview, setSessionPhotoPreview] = useState<string | null>(null);
  const [sessionPhotoMap, setSessionPhotoMap] = useState<Record<number, string>>({});
  const [approved, setApproved] = useState(false);
  const [sessionApproved, setSessionApproved] = useState(false);
  const [nowMs, setNowMs] = useState(() => Date.now());
  const locallyEndedSessionIdsRef = useRef<Set<number>>(new Set());

  const playgroundForm = useForm<PlaygroundFormValues>({
    defaultValues: { name: '', code: '', location: '', capacity: null, durationMinutes: 60, price: 100, isActive: true },
  });

  const sessionForm = useForm<IPlaygroundSession>({
    defaultValues: {
      playgroundId: 0,
      childName: '',
      childAge: null,
      guardianName: '',
      guardianPhone: '',
      startTime: formatDateTimeLocalInput(new Date()),
      durationMinutes: 60,
      price: 100,
      notes: '',
    },
  });

  const activeCount = useMemo(() => sessions.length, [sessions]);
  const loggedUserGuardianName = useMemo(() => {
    const fullName = `${currentUser?.firstName ?? ''} ${currentUser?.lastName ?? ''}`.trim();
    return fullName || currentUser?.userName || currentUser?.email || '';
  }, [currentUser?.email, currentUser?.firstName, currentUser?.lastName, currentUser?.userName]);
  const loggedUserGuardianPhone = currentUser?.phone ?? '';

  const fetchPlaygrounds = () => {
    axiosInstance
      .get(ApiConstants.playground.list)
      .then((res) => {
        const items = res.data ?? [];
        setPlaygrounds(items);
        setSelectedPlayground((current) => current ?? items[0] ?? null);
      })
      .catch(() => toast.error(i18n.t('errors.somethingWentWrong')));
  };

  const fetchSessions = (playgroundId?: number) => {
    if (!playgroundId) {
      setSessions([]);
      return;
    }

    axiosInstance
      .get(ApiConstants.playground.activeSessions(playgroundId))
      .then((res) => {
        const fetchedSessions: PlaygroundSessionView[] = res.data ?? [];
        setSessions((currentSessions) => {
          const fetchedIds = new Set(fetchedSessions.map((session) => Number(session.id ?? 0)).filter((id) => id > 0));
          const stillVisibleCurrentSessions = currentSessions.filter((session) => {
            const sessionId = Number(session.id ?? 0);
            return (
              session.playgroundId === playgroundId &&
              sessionId > 0 &&
              !fetchedIds.has(sessionId) &&
              !locallyEndedSessionIdsRef.current.has(sessionId)
            );
          });

          return [...fetchedSessions, ...stillVisibleCurrentSessions];
        });
      })
      .catch(() => toast.error(i18n.t('errors.somethingWentWrong')));
  };

  useEffect(() => {
    fetchPlaygrounds();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchSessions(selectedPlayground?.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPlayground?.id]);

  useEffect(() => {
    const timerId = window.setInterval(() => setNowMs(Date.now()), 1000);
    return () => window.clearInterval(timerId);
  }, []);

  useEffect(() => {
    const loadSessionPhotos = async () => {
      const sessionIds = sessions.map((session) => Number(session.id ?? 0)).filter((id) => id > 0);
      if (!sessionIds.length) {
        setSessionPhotoMap({});
        return;
      }

      try {
        const galleryMap = await loadEntityGalleryMap(sessionIds, ApiConstants.EntityTypes.PlaygroundSession, 1);
        const nextMap = Object.entries(galleryMap).reduce<Record<number, string>>((acc, [entityId, photos]) => {
          const photo = photos?.[0];
          if (photo?.id) acc[Number(entityId)] = getMetadataFileUrl(photo.id);
          return acc;
        }, {});
        setSessionPhotoMap(nextMap);
      } catch {
        setSessionPhotoMap({});
      }
    };

    loadSessionPhotos();
  }, [sessions]);

  useEffect(() => {
    return () => {
      if (sessionPhotoPreview?.startsWith('blob:')) {
        URL.revokeObjectURL(sessionPhotoPreview);
      }
    };
  }, [sessionPhotoPreview]);

  const setSessionPreview = (nextPreview: string | null) => {
    setSessionPhotoPreview((currentPreview) => {
      if (currentPreview?.startsWith('blob:') && currentPreview !== nextPreview) {
        URL.revokeObjectURL(currentPreview);
      }
      return nextPreview;
    });
  };

  const openAddPlayground = () => {
    setSelectedPlayground(null);
    playgroundForm.reset({ name: '', code: '', location: '', capacity: null, durationMinutes: 60, price: 100, isActive: true });
    setApproved(false);
    setIsPlaygroundModalOpen(true);
  };

  const openEditPlayground = (playground: PlaygroundFormValues) => {
    setSelectedPlayground(playground);
    playgroundForm.reset({
      name: playground.name,
      code: playground.code ?? '',
      location: playground.location ?? '',
      capacity: playground.capacity ?? null,
      durationMinutes: playground.durationMinutes ?? 60,
      price: playground.price ?? 100,
      isActive: playground.isActive ?? true,
    });
    setApproved(false);
    setIsPlaygroundModalOpen(true);
  };

  const openDeletePlayground = (playground: PlaygroundFormValues) => {
    setSelectedPlayground(playground);
    setIsDeleteModalOpen(true);
  };

  const savePlayground: SubmitHandler<PlaygroundFormValues> = (data) => {
    axiosInstance
      .post(ApiConstants.playground.update, {
        id: selectedPlayground?.id ?? 0,
        name: data.name,
        code: data.code?.trim() || null,
        location: data.location?.trim() || null,
        capacity: data.capacity === undefined || data.capacity === null || String(data.capacity) === '' ? null : Number(data.capacity),
        durationMinutes: parsePositiveNumber(data.durationMinutes, 60),
        price: parsePositiveNumber(data.price, 100),
        isActive: data.isActive ?? true,
      })
      .then(() => {
        toast.success(i18n.t('playground.saved', { defaultValue: 'Playground saved successfully' }));
        setIsPlaygroundModalOpen(false);
        fetchPlaygrounds();
      })
      .catch(() => toast.error(i18n.t('errors.somethingWentWrong')));
  };

  const deletePlayground = () => {
    if (!selectedPlayground?.id) return;

    axiosInstance
      .post(ApiConstants.playground.delete, { id: selectedPlayground.id })
      .then(() => {
        toast.success(i18n.t('playground.deleted', { defaultValue: 'Playground deleted successfully' }));
        setIsDeleteModalOpen(false);
        setSelectedPlayground(null);
        fetchPlaygrounds();
      })
      .catch(() => toast.error(i18n.t('playground.deleteBlocked', { defaultValue: 'Playground has active sessions.' })));
  };

  const openSessionModal = () => {
    if (!selectedPlayground?.id) return;
    const defaultDurationMinutes = parsePositiveNumber(selectedPlayground.durationMinutes, 60);
    const defaultPrice = parsePositiveNumber(selectedPlayground.price, 100);
    setSelectedSession(null);
    sessionForm.reset({
      playgroundId: selectedPlayground.id,
      childName: '',
      childAge: null,
      guardianName: loggedUserGuardianName,
      guardianPhone: loggedUserGuardianPhone,
      startTime: formatDateTimeLocalInput(new Date()),
      durationMinutes: defaultDurationMinutes,
      price: defaultPrice,
      notes: '',
    });
    setSessionPhotoFile(null);
    setSessionPreview(null);
    setSessionApproved(false);
    setIsSessionModalOpen(true);
  };

  const openEditSessionModal = (session: PlaygroundSessionView) => {
    setSelectedSession(session);
    sessionForm.reset({
      id: session.id,
      playgroundId: session.playgroundId,
      childName: session.childName ?? '',
      childAge: session.childAge ?? null,
      guardianName: loggedUserGuardianName,
      guardianPhone: loggedUserGuardianPhone,
      startTime: session.startTime ? formatDateTimeLocalInput(new Date(session.startTime)) : formatDateTimeLocalInput(new Date()),
      durationMinutes:
        session.startTime && session.endTime
          ? Math.max(1, Math.round((new Date(session.endTime).getTime() - new Date(session.startTime).getTime()) / 60000))
          : 60,
      price: session.price ?? 100,
      notes: session.notes ?? '',
    });
    setSessionPhotoFile(null);
    setSessionPreview(session.id ? sessionPhotoMap[session.id] ?? null : null);
    setSessionApproved(false);
    setIsSessionModalOpen(true);
  };

  const uploadSessionPhoto = async (sessionId: number) => {
    if (!sessionPhotoFile) return;

    const formData = new FormData();
    formData.append('file', sessionPhotoFile);
    formData.append('EntityId', String(sessionId));
    formData.append('EntityType', String(ApiConstants.EntityTypes.PlaygroundSession));

    await axiosInstance.post(ApiConstants.file.upload, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  };

  const startSession: SubmitHandler<IPlaygroundSession> = async (data) => {
    try {
      const response = await axiosInstance.post(ApiConstants.playground.startSession, {
        id: selectedSession?.id ?? 0,
        playgroundId: selectedPlayground?.id,
        childName: data.childName?.trim(),
        childAge: data.childAge === undefined || data.childAge === null || String(data.childAge) === '' ? null : Number(data.childAge),
        guardianName: loggedUserGuardianName || null,
        guardianPhone: loggedUserGuardianPhone || null,
        startTime: data.startTime || undefined,
        durationMinutes: parsePositiveNumber(data.durationMinutes, parsePositiveNumber(selectedPlayground?.durationMinutes, 60)),
        price: parsePositiveNumber(data.price, parsePositiveNumber(selectedPlayground?.price, 100)),
        notes: data.notes?.trim() || null,
      });

      const sessionId = Number(response.data?.id ?? 0);
      if (sessionId && sessionPhotoFile) {
        try {
          await uploadSessionPhoto(sessionId);
        } catch {
          toast.warning(i18n.t('playground.photoUploadFailed', { defaultValue: 'Session was saved, but photo upload failed.' }));
        }
      }

      toast.success(
        selectedSession?.id
          ? i18n.t('playground.sessionUpdated', { defaultValue: 'Session updated' })
          : i18n.t('playground.sessionStarted', { defaultValue: 'Session started' }),
      );
      setIsSessionModalOpen(false);
      setSelectedSession(null);
      setSessionPhotoFile(null);
      setSessionPreview(null);
      fetchSessions(selectedPlayground?.id);
    } catch {
      toast.error(i18n.t('errors.somethingWentWrong'));
    }
  };

  const handleSessionPhotoChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] ?? null;
    event.target.value = '';

    if (!file) {
      setSessionPhotoFile(null);
      setSessionPreview(selectedSession?.id ? sessionPhotoMap[selectedSession.id] ?? null : null);
      return;
    }

    try {
      const compressed = file.type.startsWith('image/') ? await compressImageIfNeeded(file, { maxKB: DEFAULT_IMAGE_MAX_KB }) : file;

      if (compressed.size > MAX_SESSION_PHOTO_BYTES) {
        toast.warning(i18n.t('playground.photoTooLarge', { defaultValue: 'Photo must be 200KB or smaller.' }));
        return;
      }

      setSessionPhotoFile(compressed);
      setSessionPreview(URL.createObjectURL(compressed));
    } catch {
      toast.error(i18n.t('playground.photoPrepareFailed', { defaultValue: 'Photo could not be prepared.' }));
    }
  };

  const endSession = (session: PlaygroundSessionView) => {
    if (!session.id) return;

    axiosInstance
      .post(ApiConstants.playground.endSession, { id: session.id })
      .then(() => {
        locallyEndedSessionIdsRef.current.add(Number(session.id));
        setSessions((currentSessions) => currentSessions.filter((currentSession) => currentSession.id !== session.id));
        toast.success(i18n.t('playground.sessionEnded', { defaultValue: 'Session closed' }));
      })
      .catch(() => toast.error(i18n.t('errors.somethingWentWrong')));
  };

  return (
    <BasicCli>
      <div className="playgroundsPage">
        <div className="playgroundsShell">
          <div className="playgroundsPanel">
            <div className="ui-panel ui-toolbar playgroundsToolbar">
              <div className="playgroundsTitleBlock">
                <h2 className="playgroundsTitle">{i18n.t('playground.title', { defaultValue: 'Playgrounds' })}</h2>
                <p className="playgroundsSubtitle">{i18n.t('playground.subtitle', { defaultValue: 'Manage company playgrounds' })}</p>
              </div>
              <div className="playgroundsActions">
                <button type="button" className="ui-control ui-btn ui-btn--primary" onClick={openAddPlayground}>
                  {i18n.t('playground.add', { defaultValue: 'Add' })}
                </button>
              </div>
            </div>

            <div className="ui-panel playgroundList">
              {playgrounds.map((playground) => (
                <div
                  role="button"
                  tabIndex={0}
                  key={playground.id}
                  className={`playgroundItem ${selectedPlayground?.id === playground.id ? 'isSelected' : ''}`}
                  onClick={() => setSelectedPlayground(playground)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      setSelectedPlayground(playground);
                    }
                  }}
                >
                  <span className="playgroundItemTop">
                    <span>
                      <span className="playgroundName">{playground.name}</span>
                      <span className="playgroundMeta">{playground.location || playground.code || '-'}</span>
                    </span>
                    <span className="playgroundBadge">{playground.capacity || '-'} cap</span>
                  </span>
                  <span className="playgroundItemActions">
                    <Tooltip title={i18n.t('generic.edit', { defaultValue: 'Edit' })}>
                      <IconButton
                        className="gtIconBtn"
                        size="small"
                        onClick={(event) => {
                          event.stopPropagation();
                          openEditPlayground(playground);
                        }}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={i18n.t('generic.delete', { defaultValue: 'Delete' })}>
                      <IconButton
                        className="gtIconBtn isDanger"
                        size="small"
                        onClick={(event) => {
                          event.stopPropagation();
                          openDeletePlayground(playground);
                        }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </span>
                </div>
              ))}
              {!playgrounds.length && <div className="sessionEmpty">{i18n.t('playground.empty', { defaultValue: 'No playgrounds yet.' })}</div>}
            </div>
          </div>

          <div className="playgroundsPanel">
            <div className="ui-panel playgroundSessionsHeader">
              <div>
                <h3>{selectedPlayground?.name ?? i18n.t('playground.select', { defaultValue: 'Select a playground' })}</h3>
                <p>
                  {i18n.t('playground.activeChildren', {
                    count: activeCount,
                    defaultValue: `${activeCount} children currently playing`,
                  })}
                </p>
              </div>
              <div className="playgroundsActions">
                {selectedPlayground && (
                  <button type="button" className="ui-control ui-btn ui-btn--primary" onClick={openSessionModal}>
                    {i18n.t('playground.addChild', { defaultValue: 'Add child' })}
                  </button>
                )}
              </div>
            </div>

            <div className="ui-panel sessionTableWrap">
              <table className="sessionTable">
                <thead>
                  <tr>
                    <th>{i18n.t('playground.child', { defaultValue: 'Child' })}</th>
                    <th>{i18n.t('playground.guardian', { defaultValue: 'Guardian' })}</th>
                    <th>{i18n.t('playground.started', { defaultValue: 'Started' })}</th>
                    <th>{i18n.t('playground.ends', { defaultValue: 'Ends' })}</th>
                    <th>{i18n.t('playground.timeLeft', { defaultValue: 'Koha' })}</th>
                    <th>{i18n.t('playground.price', { defaultValue: 'Price' })}</th>
                    <th>{i18n.t('playground.notes', { defaultValue: 'Notes' })}</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {sessions.map((session) => {
                    const timeLeftMs = getTimeLeftMs(session, nowMs);
                    const isTimeExpired = timeLeftMs !== null && timeLeftMs <= 0;
                    const isEndingSoon = timeLeftMs !== null && timeLeftMs > 0 && timeLeftMs <= ENDING_SOON_MS;
                    const sessionRowClassName = isTimeExpired ? 'sessionExpired' : isEndingSoon ? 'sessionEndingSoon' : '';

                    return (
                      <tr key={session.id} className={sessionRowClassName}>
                        <td>
                          <div className="sessionChildCell">
                            {session.id && sessionPhotoMap[session.id] ? (
                              <img className="sessionPhoto" src={sessionPhotoMap[session.id]} alt={session.childName} />
                            ) : (
                              <span className="sessionPhotoPlaceholder">{session.childName?.charAt(0)?.toUpperCase() || '-'}</span>
                            )}
                            <div>
                              <div className="sessionChildName">{session.childName}</div>
                              <div className="playgroundMeta">{session.childAge ? `${session.childAge} years` : '-'}</div>
                            </div>
                          </div>
                        </td>
                        <td>
                          <div>{session.guardianName || '-'}</div>
                          <div className="playgroundMeta">{session.guardianPhone || '-'}</div>
                        </td>
                        <td>{formatDateTime(session.startTime)}</td>
                        <td>{formatDateTime(session.endTime)}</td>
                        <td>
                          <span className={`sessionTimeLeft ${isTimeExpired ? 'isExpired' : isEndingSoon ? 'isEndingSoon' : ''}`}>
                            {formatTimeLeft(timeLeftMs)}
                          </span>
                        </td>
                        <td>{Number(session.price ?? 0).toLocaleString()}</td>
                        <td>{session.notes || '-'}</td>
                        <td>
                          <div className="sessionRowActions">
                            <Tooltip title={i18n.t('generic.edit', { defaultValue: 'Edit' })}>
                              <IconButton className="gtIconBtn" size="small" onClick={() => openEditSessionModal(session)}>
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <button type="button" className="ui-control ui-btn" onClick={() => endSession(session)}>
                              {i18n.t('playground.endSession', { defaultValue: 'End' })}
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                  {!sessions.length && (
                    <tr>
                      <td colSpan={8}>
                        <div className="sessionEmpty">
                          {selectedPlayground
                            ? i18n.t('playground.noActiveSessions', { defaultValue: 'No children are currently playing here.' })
                            : i18n.t('playground.selectFirst', { defaultValue: 'Choose a playground to see active children.' })}
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {isPlaygroundModalOpen && (
          <EditModal
            title={i18n.t('playground.playground', { defaultValue: 'Playground' })}
            width="560px"
            closeModal={() => setIsPlaygroundModalOpen(false)}
            onSubmitData={() => setApproved(true)}
            setIsModalOpen={setIsPlaygroundModalOpen}
            modalType={selectedPlayground?.id ? eFormType.Edit : eFormType.New}
          >
            <PlaygroundForm
              control={playgroundForm.control}
              errors={playgroundForm.formState.errors}
              approved={approved}
              onSubmit={() => playgroundForm.handleSubmit(savePlayground)()}
            />
          </EditModal>
        )}

        {isSessionModalOpen && (
          <EditModal
            title={
              selectedSession?.id
                ? i18n.t('playground.editChild', { defaultValue: 'Edit child' })
                : i18n.t('playground.addChild', { defaultValue: 'Add child' })
            }
            width="560px"
            closeModal={() => setIsSessionModalOpen(false)}
            onSubmitData={() => setSessionApproved(true)}
            setIsModalOpen={setIsSessionModalOpen}
            modalType={selectedSession?.id ? eFormType.Edit : eFormType.New}
          >
            <SessionForm
              control={sessionForm.control}
              errors={sessionForm.formState.errors}
              approved={sessionApproved}
              onSubmit={() => sessionForm.handleSubmit(startSession)()}
              photoPreview={sessionPhotoPreview}
              onPhotoChange={handleSessionPhotoChange}
              guardianName={loggedUserGuardianName}
              guardianPhone={loggedUserGuardianPhone}
            />
          </EditModal>
        )}

        {isDeleteModalOpen && selectedPlayground && (
          <DeleteModal width="500px" handleClose={() => setIsDeleteModalOpen(false)} handleDelete={deletePlayground}>
            {i18n.t('playground.deleteConfirm', {
              name: selectedPlayground.name,
              defaultValue: `Delete ${selectedPlayground.name}?`,
            })}
          </DeleteModal>
        )}
      </div>
    </BasicCli>
  );
};

const PlaygroundForm: React.FC<{ control: any; errors: any; approved: boolean; onSubmit: () => void }> = ({
  control,
  errors,
  approved,
  onSubmit,
}) => {
  const { i18n } = useTranslation();

  useEffect(() => {
    if (approved) onSubmit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [approved]);

  return (
    <div className="playgroundForm">
      <GeneralInput
        required
        label={i18n.t('playground.name', { defaultValue: 'Name' })}
        name="name"
        type="text"
        placeholder={i18n.t('playground.name', { defaultValue: 'Name' })}
        errors={errors}
        control={control}
      />
      <div className="playgroundFieldGrid">
        <GeneralInput
          label={i18n.t('playground.code', { defaultValue: 'Code' })}
          name="code"
          type="text"
          placeholder={i18n.t('playground.code', { defaultValue: 'Code' })}
          errors={errors}
          control={control}
        />
        <GeneralInput
          label={i18n.t('playground.capacity', { defaultValue: 'Capacity' })}
          name="capacity"
          type="number"
          placeholder={i18n.t('playground.capacity', { defaultValue: 'Capacity' })}
          errors={errors}
          control={control}
        />
      </div>
      <div className="playgroundFieldGrid">
        <GeneralInput
          label={i18n.t('playground.durationMinutes', { defaultValue: 'Duration (minutes)' })}
          name="durationMinutes"
          type="number"
          placeholder="60"
          errors={errors}
          control={control}
        />
        <GeneralInput
          label={i18n.t('playground.price', { defaultValue: 'Price' })}
          name="price"
          type="number"
          placeholder="100"
          errors={errors}
          control={control}
        />
      </div>
      <GeneralInput
        label={i18n.t('playground.location', { defaultValue: 'Location' })}
        name="location"
        type="text"
        placeholder={i18n.t('playground.location', { defaultValue: 'Location' })}
        errors={errors}
        control={control}
      />
    </div>
  );
};

const SessionForm: React.FC<{
  control: any;
  errors: any;
  approved: boolean;
  onSubmit: () => void;
  photoPreview: string | null;
  onPhotoChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  guardianName: string;
  guardianPhone: string;
}> = ({ control, errors, approved, onSubmit, photoPreview, onPhotoChange, guardianName, guardianPhone }) => {
  const { i18n } = useTranslation();

  useEffect(() => {
    if (approved) onSubmit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [approved]);

  return (
    <div className="playgroundSessionForm">
      <GeneralInput
        required
        label={i18n.t('playground.childName', { defaultValue: 'Child name' })}
        name="childName"
        type="text"
        placeholder={i18n.t('playground.childName', { defaultValue: 'Child name' })}
        errors={errors}
        control={control}
      />
      <div className="playgroundFieldGrid">
        <GeneralInput
          label={i18n.t('playground.childAge', { defaultValue: 'Age' })}
          name="childAge"
          type="number"
          placeholder={i18n.t('playground.childAge', { defaultValue: 'Age' })}
          errors={errors}
          control={control}
        />
        <GeneralInput
          label={i18n.t('playground.startTime', { defaultValue: 'Start time' })}
          name="startTime"
          type="datetime-local"
          placeholder={i18n.t('playground.startTime', { defaultValue: 'Start time' })}
          errors={errors}
          control={control}
        />
      </div>
      <div className="playgroundFieldGrid">
        <GeneralInput
          label={i18n.t('playground.durationMinutes', { defaultValue: 'Duration (minutes)' })}
          name="durationMinutes"
          type="number"
          placeholder="60"
          errors={errors}
          control={control}
        />
        <GeneralInput
          label={i18n.t('playground.price', { defaultValue: 'Price' })}
          name="price"
          type="number"
          placeholder="100"
          errors={errors}
          control={control}
        />
      </div>
      <div className="playgroundPhotoField">
        <label className="giLabel" htmlFor="playground-session-photo-file">
          {i18n.t('playground.photo', { defaultValue: 'Photo' })} (max 200KB)
        </label>
        <div className="playgroundPhotoInputRow">
          <input id="playground-session-photo-file" type="file" accept="image/*" onChange={onPhotoChange} />
          <label className="ui-control ui-btn playgroundCameraButton" htmlFor="playground-session-photo-camera">
            {i18n.t('playground.takePhoto', { defaultValue: 'Take photo' })}
          </label>
          <input
            id="playground-session-photo-camera"
            className="playgroundCameraInput"
            type="file"
            accept="image/*"
            capture="environment"
            onChange={onPhotoChange}
          />
          {photoPreview && <img className="playgroundPhotoPreview" src={photoPreview} alt="Preview" />}
        </div>
      </div>
      <div className="playgroundFieldGrid">
        <GeneralInput
          disabled
          dfvalue={guardianName}
          label={i18n.t('playground.guardianName', { defaultValue: 'Guardian' })}
          name="guardianName"
          type="text"
          placeholder={i18n.t('playground.guardianName', { defaultValue: 'Guardian' })}
          errors={errors}
          control={control}
        />
        <GeneralInput
          disabled
          dfvalue={guardianPhone}
          label={i18n.t('playground.guardianPhone', { defaultValue: 'Phone' })}
          name="guardianPhone"
          type="text"
          placeholder={i18n.t('playground.guardianPhone', { defaultValue: 'Phone' })}
          errors={errors}
          control={control}
        />
      </div>
      <GeneralInput
        label={i18n.t('playground.notes', { defaultValue: 'Notes' })}
        name="notes"
        type="text"
        placeholder={i18n.t('playground.notes', { defaultValue: 'Notes' })}
        errors={errors}
        control={control}
      />
    </div>
  );
};

export default Playgrounds;
